import Notification20 from '@carbon/icons-react/es/notification/20';
import { Suspense, lazy, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CompactMenuTitle } from 'components/atoms/menu/compact-menu';
import { DrawerContent } from 'components/organisms/task-pane/task-detail/header/add-time-entry/drawer-content';
import { RoutePaths } from 'constant';
import { useNotificationsDrawerContext } from 'contexts/providers/notifications-drawer';
import { usePending } from 'stores/notifications';
import { handleLastSeenAtSubscription } from 'stores/notifications/use-pending';
import { useNotificationsDrawer } from './hash-based-notifications';
import { Drawer } from './styled';

/**
 * Cached chunk-load promise. The drawer title is rendered atomically
 * by the Drawer component, but the body (this List) is React.lazy
 * wrapped in <Suspense fallback={null}>. If the user clicks the bell
 * before the chunk arrives, the body briefly renders as nothing —
 * empty drawer with just a title — and downstream code (e.g. the
 * filter pills locator in TC-1115) sees an empty DOM at click time.
 *
 * Expose a prefetch helper so the left-nav can warm the chunk up at
 * bell-button mount (idle-callback gated). The lazy() still wraps
 * the same promise, so the code-split chunk shape is unchanged.
 */
let listChunkPromise: Promise<typeof import('../list')> | undefined;

export const prefetchNotificationsList = (): Promise<
  typeof import('../list')
> => {
  if (!listChunkPromise) {
    listChunkPromise = import('../list');
  }

  return listChunkPromise;
};

const List = lazy(() =>
  prefetchNotificationsList().then((m) => ({ default: m.List })),
);

const StyledTitle = styled.span`
  font-size: var(--heading-5-size);
`;

export const NotificationsDrawer = () => {
  const [params, , hide] = useNotificationsDrawer();
  const { visibility, setVisibility } = useNotificationsDrawerContext();

  const [pending, resetPending] = usePending();
  const refreshRef = useRef<() => void>();
  const listRef = useCallback((instance: any) => {
    if (instance?.refresh != null) {
      refreshRef.current = instance.refresh;
    }
  }, []);

  useEffect(() => {
    handleLastSeenAtSubscription();

    return () => {
      handleLastSeenAtSubscription().unsubscribe();
    };
  }, []);

  const onClose = useCallback(() => {
    setVisibility(false);
    setTimeout(hide, 100);
  }, [hide, setVisibility]);

  useEffect(() => {
    switch (params[0]) {
      case 'show':
        setVisibility(true);
        if (pending > 0) {
          resetPending();
        }
        break;

      case 'invalid':
      default:
        setVisibility(false);
    }
  }, [params, pending, resetPending, setVisibility]);

  return (
    <Drawer
      onClose={onClose}
      destroyOnClose
      open={visibility}
      title={
        <CompactMenuTitle
          icon={<Notification20 />}
          text={
            <StyledTitle>
              <FormattedMessage id="notifications" />
            </StyledTitle>
          }
        />
      }
    >
      <DrawerContent.Content>
        <Suspense fallback={null}>
          <List
            ref={listRef}
            pending={pending}
            configScreenUrl={RoutePaths.notificationsSettings}
            resetPending={resetPending}
          />
        </Suspense>
      </DrawerContent.Content>
    </Drawer>
  );
};
